import React, { useCallback, useMemo, useState } from "react";
import CabinetFreshness from "../components/Cabinet/CabinetFreshness";
import CabinetGraphChart from "../components/Cabinet/CabinetGraphChart";
import CabinetLegend from "../components/Cabinet/CabinetLegend";
import CabinetNodePanel from "../components/Cabinet/CabinetNodePanel";
import CabinetSummaryStrip from "../components/Cabinet/CabinetSummaryStrip";
import { useCabinetData } from "../components/Cabinet/useCabinetData";
import ErrorDisplay from "../components/ErrorDisplay";
import NoDataState from "../components/PortfolioManagement/NoDataState";

/**
 * Cabinet v1 — the catalog graph over the ingested deal rooms.
 *
 * Read-only projection of the `cabinet_*` tables: deals, logical documents (each
 * carrying its physical version chain) and entities, joined by typed edges.
 * Nothing here writes; the DB rows are the truth and this is one more view of
 * them alongside the OKF bundle.
 *
 * Gated by `REACT_APP_CABINET_UI_ENABLED` at the nav + route level, so the page
 * itself assumes it is reachable.
 *
 * The data is polled and stale-while-revalidate (see `useCabinetData`): ingest
 * happens in the background, so what is on screen goes out of date on its own,
 * and a refetch must never blank a view the user is reading.
 */
const Cabinet: React.FC = () => {
	const {
		graph,
		summary,
		loading,
		error,
		refreshing,
		stale,
		lastUpdatedAt,
		refresh,
	} = useCabinetData();
	const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

	const selectedNode = useMemo(() => {
		if (graph === null || selectedNodeId === null) return null;
		return graph.nodes.find((node) => node.id === selectedNodeId) ?? null;
	}, [graph, selectedNodeId]);

	const clearSelection = useCallback(() => setSelectedNodeId(null), []);

	return (
		<div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-gray-50">
			<header className="px-6 pb-3 pt-6">
				<h1 className="text-xl font-semibold text-text-base">Cabinet</h1>
				<p className="mt-1 text-sm text-text-muted">
					The deal rooms as a catalog — deals, documents and entities, with the
					version chain behind every document.
				</p>
			</header>

			{error && graph === null ? (
				<ErrorDisplay error={error} onRetry={refresh} />
			) : loading && graph === null ? (
				<div className="px-6 pb-6">
					<div className="h-[76px] animate-pulse rounded-lg border border-gray-200 bg-white" />
					<div className="mt-4 h-[420px] animate-pulse rounded-lg border border-gray-200 bg-white" />
				</div>
			) : graph === null || graph.nodes.length === 0 ? (
				<NoDataState
					message="Nothing in the cabinet yet — upload files to a project and they will appear here."
					onRefresh={refresh}
				/>
			) : (
				<div className="flex min-h-0 flex-1 flex-col px-6 pb-6">
					<div className="mb-2 flex items-center justify-end">
						<CabinetFreshness
							lastUpdatedAt={lastUpdatedAt}
							refreshing={refreshing}
							stale={stale}
							onRefresh={refresh}
						/>
					</div>
					{summary && <CabinetSummaryStrip summary={summary} />}

					<div className="mt-4 flex min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white">
						<div className="flex min-w-0 flex-1 flex-col">
							<div className="min-h-0 flex-1">
								<CabinetGraphChart
									graph={graph}
									selectedNodeId={selectedNodeId}
									onSelectNode={setSelectedNodeId}
								/>
							</div>
							<CabinetLegend />
						</div>

						{selectedNode && (
							<div className="w-[360px] shrink-0">
								<CabinetNodePanel
									node={selectedNode}
									onClose={clearSelection}
								/>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default Cabinet;
